import type { ISchedule } from '@/entities/booking/model/types';

interface Options {
  isWork: number[] | 'all' | 'none';
  is24HoursWork: number[] | 'all' | 'none';
  startDate?: Date;
  daysCount?: number;
  startHour?: number | number[];
  endHour?: number | number[];
  sessionDuration?: number;
}

interface TimeslotsOptions {
  is24Hours: boolean;
  startHour: number;
  endHour: number;
  sessionDuration: number;
}

const generateTimeslots = (options: TimeslotsOptions): string[] => {
  const { is24Hours, startHour, endHour, sessionDuration } = options;
  const timeslots: string[] = [];

  if (is24Hours) {
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += sessionDuration) {
        timeslots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
      }
    }

    return timeslots;
  }

  if (startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23 || sessionDuration <= 0 || sessionDuration > 60) {
    return timeslots;
  }

  const hoursRange = endHour > startHour ? { start: startHour, end: endHour } : { start: startHour, end: endHour + 24 };

  for (let h = hoursRange.start; h < hoursRange.end; h++) {
    const hour = h % 24;

    for (let minute = 0; minute < 60; minute += sessionDuration) {
      if (h === hoursRange.end - 1 && minute + sessionDuration > 60) {
        continue;
      }

      timeslots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }

  return timeslots;
};

const getIsWork = (day: number, data: number[] | 'all' | 'none') => {
  if (data === 'all') return true;
  if (data === 'none') return false;

  return data.includes(day);
};

const getValueForDay = (day: number, value: number | number[]): number => {
  if (Array.isArray(value)) {
    return value[Math.min(day - 1, value.length - 1)];
  }
  return value;
};

export const genDoctorBookingSchedule = (options: Options): ISchedule[] => {
  const {
    isWork = 'all',
    is24HoursWork = 'none',
    startDate = new Date(),
    daysCount = 8,
    startHour = 8,
    endHour = 14,
    sessionDuration = 30,
  } = options;

  const schedule: ISchedule[] = [];
  const currentDate = new Date(startDate);

  for (let i = 0; i < daysCount; i++) {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + i);

    const day = date.getDay() || 7;
    const dayOfMonth = date.getDate();
    const dateString = date.toISOString().split('T')[0];

    const isDayWork = getIsWork(day, isWork);
    const isDayWith24HoursWork = getIsWork(day, is24HoursWork);

    let timeslots: string[] = [];

    if (isDayWork) {
      const dayStartHour = getValueForDay(day, startHour);
      const dayEndHour = getValueForDay(day, endHour);

      timeslots = generateTimeslots({
        is24Hours: isDayWith24HoursWork,
        startHour: dayStartHour,
        endHour: dayEndHour,
        sessionDuration,
      });
    }

    schedule.push({
      day,
      dayOfMonth,
      date: dateString,
      isWork: isDayWork,
      timeslots,
      is24HoursWork: isDayWith24HoursWork,
    });
  }

  return schedule;
};
